// views/Dashboard/PainelExecutivo/areas/AreaMetas.tsx
//
// Aba Metas (pedido do Felipe em 28/10/2026, #6496): o lugar único onde se define
// a meta de cada indicador, mês a mês. A tela inicial (Diretoria, Gestão
// Comercial) e os KPIs da aba CEO leem daqui.
//
// Uma meta vale do mês em que foi definida em diante, até outra ser definida:
// mudar novembro não reescreve outubro. Assim o mês passado continua sendo
// comparado com a meta que valia nele, e dá para ver, mês a mês, se o resultado
// acompanhou a meta.
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ChevronLeft, ChevronRight, CornerDownRight, Loader2, Target, X } from 'lucide-react';
import { useAuth } from '../../../../src/contexts/AuthContext';
import { useToast } from '../../../../src/contexts/ToastContext';
import {
  useGeracaoCeo, useKpisDepartamento, useMesCorrente, useMetasFaturamento, useParadasCeo, useUsinasCeo,
} from '../sections/ceo/useCeoDados';
import { useContextoKpis } from '../sections/ceo/useContextoKpis';
import { useFinanceiroDashboardData } from '../sections/financeiro/useFinanceiroDashboardData';
import { useMetasMensais } from '../sections/metas/useMetasMensais';
import { Bloco, BotaoPrimario, BotaoSecundario, COR_FAIXA, ErroBloco, LinhasCarregando } from '../sections/ceo/ui';
import { agruparParadas, ultimoMesFechado } from '../../../../src/lib/dashboard/painelCeo';
import { UNIDADES_KPI, formatarValorKpi } from '../../../../src/lib/dashboard/kpisDepartamento';
import {
  indicadoresDeMeta, lerNumeroBr, mesDaData, mesesAoRedor, metaDoMes, rotuloDoMes, somarMeses,
  type IndicadorDeMeta,
} from '../../../../src/lib/dashboard/metasMensais';
import { avaliarKpi } from '../../../../src/lib/visaoGeral/diretoria';

const CAMPO =
  'w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none transition-[border-color,box-shadow] placeholder:text-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-900 dark:text-white';

const SITUACAO: Record<IndicadorDeMeta['situacao'], { rotulo: string; classe: string }> = {
  validado: { rotulo: 'KPI validado', classe: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  proposto: { rotulo: 'Aguardando validação', classe: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  sugestao: { rotulo: 'Sugestão da aba CEO', classe: 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300' },
  fixo: { rotulo: 'Usado na tela inicial', classe: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
};

const escreverNumero = (v: number | null | undefined) =>
  v == null ? '' : Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 2 });

const dataHora = (iso: string) => new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });

interface Edicao { indicador: IndicadorDeMeta; mes: string }

const AreaMetas: React.FC = () => {
  const { profile } = useAuth();
  const { addToast } = useToast();
  const podeEditar = profile?.role === 'adm';

  // Os mesmos dados da aba CEO: o "agora" de cada indicador e a meta sugerida saem daqui.
  const { agora, inicio, fim } = useMesCorrente();
  const { usinas, fator } = useUsinasCeo();
  const geracao = useGeracaoCeo(usinas, inicio, fim);
  const paradas = useParadasCeo(usinas, inicio, agora);
  const metasFaturamento = useMetasFaturamento();
  const { dados } = useFinanceiroDashboardData();
  const eventos = useMemo(() => agruparParadas(paradas.episodios, usinas), [paradas.episodios, usinas]);
  const fechado = useMemo(() => ultimoMesFechado(dados, agora), [dados, agora]);
  const ctx = useContextoKpis({
    agora, usinas, fator, linhas: geracao.linhas, eventos, paradasCarregando: paradas.loading, mesFechado: fechado, metas: metasFaturamento,
  });

  const kpis = useKpisDepartamento();
  const mm = useMetasMensais();
  const mesAtual = mesDaData(agora);
  const [centro, setCentro] = useState(mesAtual);
  const meses = useMemo(() => mesesAoRedor(centro, 2, 3), [centro]);
  const departamentos = useMemo(() => indicadoresDeMeta(kpis.kpis), [kpis.kpis]);
  const [edicao, setEdicao] = useState<Edicao | null>(null);

  const definidas = mm.metas.filter((m) => m.mes.slice(0, 7) === mesAtual).length;
  const vigentes = departamentos.flatMap((d) => d.indicadores).filter((i) => metaDoMes(mm.metas, i.chave, mesAtual)).length;
  const totalIndicadores = departamentos.reduce((t, d) => t + d.indicadores.length, 0);

  return (
    <div className="space-y-5">
      <Bloco
        titulo="Metas mês a mês"
        subtitulo={
          <>
            A meta de um mês vale para os seguintes até outra ser definida. Mudar a de novembro não muda outubro: cada mês continua
            comparado com a meta que valia nele. Sem meta definida, as telas usam a meta validada do KPI na aba CEO ou, sem ela, a sugerida.
          </>
        }
        destaque={mm.carregando ? null : (
          <div>
            <p className="font-montserrat text-[28px] font-bold leading-none tabular-nums text-gray-900 dark:text-white">{vigentes}/{totalIndicadores}</p>
            <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
              indicadores com meta em {rotuloDoMes(mesAtual)}{definidas ? ` · ${definidas} definida${definidas === 1 ? '' : 's'} neste mês` : ''}
            </p>
          </div>
        )}
        acoes={
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => setCentro(somarMeses(centro, -1))} aria-label="Mês anterior"
              className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"><ChevronLeft size={16} /></button>
            <span className="min-w-[150px] text-center text-xs font-semibold tabular-nums text-gray-600 dark:text-gray-300">
              {rotuloDoMes(meses[0])} a {rotuloDoMes(meses[meses.length - 1])}
            </span>
            <button type="button" onClick={() => setCentro(somarMeses(centro, 1))} aria-label="Próximo mês"
              className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"><ChevronRight size={16} /></button>
            {centro !== mesAtual && (
              <button type="button" onClick={() => setCentro(mesAtual)} className="ml-1 text-xs font-semibold text-primary hover:underline">Mês atual</button>
            )}
          </div>
        }
      >
        {mm.semTabela && (
          <div className="mx-5 mb-5 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200">
            <AlertTriangle size={15} className="mt-0.5 shrink-0" />
            <span>
              A tabela de metas ainda não existe no banco: falta aplicar a migration <code>20261028120000_metas_mensais.sql</code>.
              Até lá dá para ver os indicadores, mas não para gravar meta; as telas seguem com a meta validada ou a sugerida.
            </span>
          </div>
        )}
        {mm.erro && <ErroBloco mensagem={mm.erro} />}
      </Bloco>

      {kpis.erro ? (
        <ErroBloco mensagem={kpis.erro} />
      ) : kpis.loading || mm.carregando ? (
        <LinhasCarregando linhas={6} rotulo="Carregando as metas…" />
      ) : (
        departamentos.map((dep) => (
          <Bloco key={dep.id} titulo={dep.rotulo}
            subtitulo={dep.id === 'comercial' ? (
              <>
                A energia aprovada é a meta do time, lida pela tela da Talita e pelo card de energia da Diretoria. Inclui as licitações,
                mostradas à parte nas telas. Metas por vendedor (base da comissão) ficam em{' '}
                <Link to="/financeiro/folha-pagamento" className="font-semibold text-primary hover:underline">Folha de Pagamento › Vendedores</Link>.
              </>
            ) : dep.id === 'gestao' ? (
              <>A meta de faturamento por usina fica na aba <Link to="/dashboard/painel-executivo/ceo" className="font-semibold text-primary hover:underline">CEO</Link>; aqui vale a do total.</>
            ) : undefined}
          >
            <div className="overflow-x-auto border-t border-gray-100 dark:border-gray-700">
              {/* Larguras fixas: os meses ficam alinhados de um departamento para o outro. */}
              <table className="w-full min-w-[760px] table-fixed text-sm">
                <colgroup>
                  <col className="w-[26%]" />
                  <col className="w-[14%]" />
                  {meses.map((m) => <col key={m} />)}
                </colgroup>
                <thead>
                  <tr className="text-[11px] uppercase tracking-wider text-gray-400">
                    <th className="px-5 py-2.5 text-left font-semibold">Indicador</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Agora</th>
                    {meses.map((m) => (
                      <th key={m} className={`px-3 py-2.5 text-right font-semibold tabular-nums ${m === mesAtual ? 'text-primary' : ''}`}>
                        {rotuloDoMes(m).replace('/20', '/')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {dep.indicadores.map((ind) => {
                    const agoraKpi = avaliarKpi(ind.def, ctx, ind.kpi, mm.metas);
                    const sugerida = ind.def && !agoraKpi?.carregando ? (() => { const v = ind.def!.calcular(ctx); return v ? ind.def!.metaSugerida(v, ctx) : null; })() : null;
                    const situacao = SITUACAO[ind.situacao];
                    return (
                      <tr key={ind.chave} className="align-top">
                        <td className="px-5 py-3">
                          <p className="font-semibold text-gray-800 dark:text-gray-100" title={ind.def?.como_medir ?? ind.kpi?.como_medir ?? undefined}>{ind.nome}</p>
                          <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${situacao.classe}`}>{situacao.rotulo}</span>
                        </td>
                        <td className="px-3 py-3 text-right tabular-nums">
                          {ind.def?.semValorNoPainel ? (
                            <span className="text-gray-400" title="O valor aparece na Visão Geral; aqui só se define a meta.">—</span>
                          ) : !agoraKpi || agoraKpi.carregando ? (
                            <span className="text-gray-300">…</span>
                          ) : (
                            <>
                              <span className="font-semibold text-gray-900 dark:text-white">{formatarValorKpi(agoraKpi.atual, ind.unidade)}</span>
                              {agoraKpi.origemMeta === 'sugerida' ? (
                                <span className="block text-[11px] text-gray-400" title="Sem meta definida: as telas comparam com a sugerida pelo sistema">sem meta definida</span>
                              ) : agoraKpi.atingimento != null && agoraKpi.meta > 0 && (
                                <span className={`block text-[11px] font-semibold ${COR_FAIXA[agoraKpi.faixa].texto}`}
                                  title={`Contra a meta ${agoraKpi.metaAteAgora < agoraKpi.meta ? 'proporcional aos dias corridos' : 'do período'}`}>
                                  {Math.round(agoraKpi.atingimento * 100)}% da meta
                                </span>
                              )}
                              <span className="block text-[10px] text-gray-400">{agoraKpi.periodo}</span>
                            </>
                          )}
                        </td>
                        {meses.map((m) => {
                          const doMes = metaDoMes(mm.metas, ind.chave, m);
                          const aberta = edicao?.indicador.chave === ind.chave && edicao.mes === m;
                          const base = ind.metaBase;
                          const conteudo = doMes ? (
                            doMes.herdada ? (
                              <span className="inline-flex items-center gap-0.5 text-gray-400" title={`Repete a meta definida em ${rotuloDoMes(doMes.desde)}`}>
                                <CornerDownRight size={11} /> {formatarValorKpi(doMes.valor, ind.unidade)}
                              </span>
                            ) : (
                              <span className="font-semibold text-gray-900 dark:text-white"
                                title={`Definida em ${dataHora(doMes.linha.atualizado_em)}${doMes.linha.observacao ? ` · ${doMes.linha.observacao}` : ''}`}>
                                {formatarValorKpi(doMes.valor, ind.unidade)}
                              </span>
                            )
                          ) : base != null ? (
                            <span className="italic text-gray-400" title={ind.situacao === 'validado' ? 'Sem meta no mês: vale a meta validada do KPI' : 'Sem meta no mês: vale a meta proposta do KPI'}>
                              {formatarValorKpi(base, ind.unidade)}
                            </span>
                          ) : m === mesAtual && sugerida != null ? (
                            <span className="italic text-gray-400" title="Sem meta: vale a sugerida pelo sistema">{formatarValorKpi(sugerida, ind.unidade)}</span>
                          ) : (
                            <span className="text-gray-300 dark:text-gray-600">—</span>
                          );
                          return (
                            <td key={m} className={`px-1.5 py-2 text-right tabular-nums ${m === mesAtual ? 'bg-primary/[0.03] dark:bg-primary-light/[0.04]' : ''}`}>
                              {podeEditar && !mm.semTabela ? (
                                <button type="button" onClick={() => setEdicao(aberta ? null : { indicador: ind, mes: m })}
                                  aria-label={`Meta de ${ind.nome} em ${rotuloDoMes(m)}`}
                                  className={`w-full rounded-lg px-1.5 py-1.5 text-right text-[13px] transition-colors hover:bg-gray-100 dark:hover:bg-gray-700/60 ${aberta ? 'ring-2 ring-primary/40' : ''}`}>
                                  {conteudo}
                                </button>
                              ) : (
                                <span className="block px-1.5 py-1.5 text-[13px]">{conteudo}</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {edicao && edicao.indicador.departamento === dep.id && (
              <EditorMeta
                key={`${edicao.indicador.chave}-${edicao.mes}`}
                edicao={edicao}
                mesAtual={mesAtual}
                metas={mm.metas}
                onFechar={() => setEdicao(null)}
                onSalvar={async (valor, observacao) => {
                  try {
                    await mm.salvar(edicao.indicador.chave, edicao.mes, valor, observacao);
                    addToast(`Meta de ${rotuloDoMes(edicao.mes)} salva.`, 'success');
                    setEdicao(null);
                  } catch (e: any) { addToast(e?.message || 'Não foi possível salvar a meta.', 'error'); }
                }}
                onApagar={async () => {
                  try {
                    await mm.apagar(edicao.indicador.chave, edicao.mes);
                    addToast(`${rotuloDoMes(edicao.mes)} volta a repetir a meta anterior.`, 'success');
                    setEdicao(null);
                  } catch (e: any) { addToast(e?.message || 'Não foi possível tirar a meta.', 'error'); }
                }}
              />
            )}
          </Bloco>
        ))
      )}
    </div>
  );
};

// ─────────────── editor de uma meta ───────────────

interface EditorProps {
  edicao: Edicao;
  mesAtual: string;
  metas: ReturnType<typeof useMetasMensais>['metas'];
  onFechar: () => void;
  onSalvar: (valor: number, observacao: string) => Promise<void>;
  onApagar: () => Promise<void>;
}

const EditorMeta: React.FC<EditorProps> = ({ edicao, mesAtual, metas, onFechar, onSalvar, onApagar }) => {
  const { indicador, mes } = edicao;
  const doMes = metaDoMes(metas, indicador.chave, mes);
  const propria = doMes && !doMes.herdada ? doMes : null;
  const [texto, setTexto] = useState(escreverNumero(doMes?.valor ?? indicador.metaBase));
  const [observacao, setObservacao] = useState(propria?.linha.observacao ?? '');
  const [ocupado, setOcupado] = useState(false);
  const valor = lerNumeroBr(texto);
  const passado = mes < mesAtual;
  const proxima = metas
    .filter((m) => m.indicador === indicador.chave && m.mes.slice(0, 7) > mes)
    .sort((a, b) => a.mes.localeCompare(b.mes))[0];
  const ate = proxima ? `até ${rotuloDoMes(proxima.mes.slice(0, 7))} (lá já existe outra meta)` : 'e os meses seguintes, até outra meta ser definida';

  const rodar = async (acao: () => Promise<void>) => { setOcupado(true); await acao(); setOcupado(false); };

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); if (valor != null) rodar(() => onSalvar(valor, observacao)); }}
      onKeyDown={(e) => { if (e.key === 'Escape') onFechar(); }}
      className="m-5 rounded-xl border border-primary/25 bg-primary/[0.03] p-4 dark:border-primary-light/25 dark:bg-primary-light/[0.04]"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
            <Target size={15} className="text-primary" /> {indicador.nome} · {rotuloDoMes(mes)}
          </p>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            Vale para {rotuloDoMes(mes)} {ate}.
            {indicador.sentido === 'menor' && ' Neste indicador, quanto menor, melhor: a meta é o teto.'}
          </p>
        </div>
        <button type="button" onClick={onFechar} aria-label="Fechar" className="rounded-md p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"><X size={15} /></button>
      </div>
      {passado && (
        <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
          {rotuloDoMes(mes)} já passou: trocar a meta muda a comparação desse mês na tela inicial.
        </p>
      )}
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-[180px_1fr]">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
            Meta em {UNIDADES_KPI.find((u) => u.id === indicador.unidade)?.rotulo ?? 'número'}
          </span>
          <input value={texto} onChange={(e) => setTexto(e.target.value)} inputMode="decimal" autoFocus
            aria-invalid={texto.trim() !== '' && valor == null}
            className={`${CAMPO} tabular-nums ${texto.trim() !== '' && valor == null ? 'border-rose-400' : ''}`} />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Por quê <span className="font-normal text-gray-400">(opcional)</span></span>
          <input value={observacao} onChange={(e) => setObservacao(e.target.value)} maxLength={200}
            placeholder="Ex.: rampa do time novo; meta do plano de bônus" className={CAMPO} />
        </label>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <BotaoPrimario type="submit" disabled={ocupado || valor == null} className="!px-4 !py-2 !text-sm">
          {ocupado && <Loader2 size={14} className="animate-spin" />} Salvar meta
        </BotaoPrimario>
        <BotaoSecundario onClick={onFechar} disabled={ocupado} className="!px-4 !py-2 !text-sm">Cancelar</BotaoSecundario>
        {propria && (
          <button type="button" disabled={ocupado} onClick={() => rodar(onApagar)}
            className="ml-auto text-xs font-semibold text-gray-500 underline-offset-2 hover:text-rose-600 hover:underline">
            Tirar a meta de {rotuloDoMes(mes)} (volta a repetir a anterior)
          </button>
        )}
      </div>
    </form>
  );
};

export default AreaMetas;
